// Contact.js
import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Clock, MapPin } from 'lucide-react';
import contactImage from '../assets/contact.png';

const Contact = () => {
  const locations = [
    {
      country: "Zimbabwe",
      code: "zw",
      email: "zw@validatesystems.com",
      phone: "+263 XXX XXX XXXX",
      hours: "Mon–Fri, 09:00–17:00 CAT",
      city: "Harare"
    },
        {
      country: "Kenya",
      code: "ke",
      email: "ke@validatesystems.com",
      phone: "+254 XXX XXX XXXX",
      hours: "Mon–Fri, 09:00–17:00 EAT",
      city: "Nairobi"
    },
    {
      country: "Ghana",
      code: "gh",
      email: "gh@validatesystems.com",
      phone: "+233 XXX XXX XXXX",
      hours: "Mon–Fri, 09:00–17:00 GMT",
      city: "Accra"
    },
    {
      country: "Nigeria",
      code: "ng",
      email: "ng@validatesystems.com",
      phone: "+234 XXX XXX XXXX",
      hours: "Mon–Fri, 09:00–17:00 WAT",
      city: "Lagos"
    },
    {
      country: "South Africa",
      code: "za",
      email: "za@validatesystems.com",
      phone: "+27 XXX XXX XXXX",
      hours: "Mon–Fri, 09:00–17:00 SAST",
      city: "Johannesburg"
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div 
              className="text-left"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <motion.h1 
                className="text-4xl md:text-5xl lg:text-5xl font-inter font-bold text-acBlack leading-tight"
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 1, delay: 0.3 }}
              >
                Get in Touch <br /><span className="text-2xl font-semibold">We're Here to Help</span>
              </motion.h1>
              
              <motion.p 
                className="text-lg text-gray-600 mt-6 max-w-2xl"
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 1, delay: 0.5 }}
              >
                Reach out to our team for verification services, travel & immigration support, or general inquiries. We operate across five African countries with local expertise and human-verified processes.
              </motion.p>

              <motion.p 
                className="text-sm text-gray-500 mt-4 max-w-2xl"
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 1, delay: 0.6 }}
              >
                Response time: 24–72 hours on business days. Urgent cases are prioritized.
              </motion.p>
            </motion.div>

            {/* Right Image */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <img
                src={contactImage}
                alt="Contact Validate Systems"
                className="w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Location-Specific Contact Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="text-left mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack">
              Our Locations
            </h2>
            <p className="text-gray-600 mt-4">
              Select your country to connect with our local team
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {locations.map((location, index) => (
              <motion.div
                key={location.code}
                className="bg-white rounded-lg p-8 shadow-sm hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-acYellow rounded-full flex items-center justify-center mr-4">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-inter font-bold text-acBlack">
                      {location.country}
                    </h3>
                    <p className="text-sm text-gray-500">{location.city}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start">
                    <Mail className="w-5 h-5 text-acYellow mr-3 mt-0.5 flex-shrink-0" />
                    <a 
                      href={`mailto:${location.email}`} 
                      className="text-gray-700 hover:text-acYellow transition-colors text-sm break-all"
                    >
                      {location.email}
                    </a>
                  </div>

                  {/* <div className="flex items-start">
                    <Phone className="w-5 h-5 text-acYellow mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">
                      {location.phone}
                    </span>
                  </div> */}

                  <div className="flex items-start">
                    <Clock className="w-5 h-5 text-acYellow mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">
                      {location.hours}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* General Contact Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack mb-4">
              General Inquiries
            </h2>
            <p className="text-gray-600">
              For general questions or if you're unsure which location to contact
            </p>
          </motion.div>

          <motion.div
            className="max-w-2xl mx-auto bg-gray-50 rounded-lg p-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="space-y-6">
              <div className="flex items-center justify-center">
                <Mail className="w-6 h-6 text-acYellow mr-3" />
                <a 
                  href="mailto:info@validatesystems.com" 
                  className="text-xl text-acBlack hover:text-acYellow transition-colors font-semibold"
                >
                  info@validatesystems.com
                </a>
              </div>
              <p className="text-center text-gray-600 text-sm">
                We'll route your inquiry to the appropriate team based on your location and needs.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;